'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import {
  authService,
  AuthState,
  UserProfile,
  SESSION_EXPIRED_EVENT,
} from '@/lib/auth-direct';

interface AuthContextType {
  user: UserProfile | null;
  tokens: { accessToken: string; refreshToken?: string } | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  updateUser: (user: UserProfile) => void;
  isAuthenticated: boolean;
}

const AuthContextDirect = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProviderDirect({ children }: AuthProviderProps) {
  const router = useRouter();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    tokens: null,
    isLoading: true,
    error: null,
  });

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = () => {
      const storedAuth = authService.getStoredAuth();
      setAuthState(storedAuth);
    };

    checkAuth();
  }, []);

  // When the API client reports an unrecoverable 401 (refresh-and-replay
  // failed), clear auth state and send the user to sign-in so the UI never
  // keeps rendering a logged-in shell with a dead session.
  useEffect(() => {
    const handleSessionExpired = () => {
      setAuthState({
        user: null,
        tokens: null,
        isLoading: false,
        error: null,
      });
      router.push('/auth/signin');
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [router]);

  const login = async (email: string, password: string): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await authService.login(email, password);

      if (result.user && result.tokens) {
        // Store authentication
        authService.storeAuth(result.user, result.tokens);
        setAuthState(result);
        return true;
      } else {
        setAuthState(result);
        return false;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'auth.loginFailed';
      setAuthState({
        user: null,
        tokens: null,
        isLoading: false,
        error: errorMessage,
      });
      return false;
    }
  };

  const logout = async () => {
    await authService.logout();
    setAuthState({
      user: null,
      tokens: null,
      isLoading: false,
      error: null,
    });
    router.push('/auth/signin');
  };

  const updateUser = (user: UserProfile) => {
    setAuthState(prev => ({
      ...prev,
      user,
    }));
    // Store the updated user data; skip if tokens are unavailable
    if (authState.tokens) {
      authService.storeAuth(user, authState.tokens);
    }
  };

  const value: AuthContextType = {
    user: authState.user,
    tokens: authState.tokens,
    isLoading: authState.isLoading,
    error: authState.error,
    login,
    logout,
    updateUser,
    isAuthenticated: !!authState.user && !!authState.tokens,
  };

  return (
    <AuthContextDirect.Provider value={value}>
      {children}
    </AuthContextDirect.Provider>
  );
}

export function useAuthDirect(): AuthContextType {
  const context = useContext(AuthContextDirect);
  if (context === undefined) {
    throw new Error('useAuthDirect must be used within an AuthProviderDirect');
  }
  return context;
}
